import { useState } from "react";
import { ChevronDownIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { useComparacion, useRevisiones } from "@/hooks/useRedaccion";
import { getApiErrorMessage } from "@/lib/api";
import { formatearFechaYHora } from "@/lib/fechas";
import { segmentosLegiblesDelDiff, type RevisionDelHistorial } from "@/lib/redaccion";

interface HistorialDeVersionesProps {
  informeId: number;
  itemId: number;
  /** Cuántas versiones tiene, para rotular el desplegable sin pedirlas. */
  total: number;
}

function autorDeLaVersion(v: RevisionDelHistorial): string {
  if (v.autor_tipo === "ia") return "IA";
  if (v.autor_tipo === "sistema") return "Sistema";

  return v.autor?.name ?? "Persona";
}

const palabras = (agregadas: number, quitadas: number) => `+${agregadas} −${quitadas} palabras`;

/**
 * «Versiones (N)»: cada texto que tuvo la sección, de la más nueva a la más
 * vieja, y la comparación de dos cualesquiera con el diff por palabras.
 *
 * Se pide al abrir: el listado sin cuerpos es barato, pero nadie lo mira en
 * cada sección que revisa.
 */
export function HistorialDeVersiones({ informeId, itemId, total }: HistorialDeVersionesProps) {
  const [abierto, setAbierto] = useState(false);
  // Las dos elegidas, en el orden en que se eligieron: una tercera suelta la primera.
  const [elegidas, setElegidas] = useState<number[]>([]);
  const [comparando, setComparando] = useState<[number, number] | null>(null);

  const revisiones = useRevisiones(informeId, itemId, abierto);
  const comparacion = useComparacion(informeId, itemId, comparando?.[0] ?? null, comparando?.[1] ?? null);

  function alternar(id: number) {
    setComparando(null);
    setElegidas((actuales) => {
      if (actuales.includes(id)) return actuales.filter((x) => x !== id);

      return [...actuales, id].slice(-2);
    });
  }

  return (
    <Collapsible open={abierto} onOpenChange={setAbierto} className="border-border border-t pt-3">
      <CollapsibleTrigger asChild>
        <Button type="button" variant="ghost" size="sm">
          Versiones ({total})
          <ChevronDownIcon aria-hidden className={abierto ? "rotate-180" : undefined} />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <section aria-label="Versiones del texto" className="mt-2 flex flex-col gap-3">
          {revisiones.isPending ? (
            <Skeleton className="h-20 w-full rounded-lg" />
          ) : revisiones.isError ? (
            <p className="text-label text-danger-soft-foreground">
              {getApiErrorMessage(revisiones.error, "No pudimos cargar las versiones.")}
            </p>
          ) : (
            <>
              <ul className="flex flex-col gap-1.5">
                {revisiones.data.map((v) => (
                  <li key={v.id} className="flex items-center gap-2.5">
                    <Checkbox
                      checked={elegidas.includes(v.id)}
                      onCheckedChange={() => alternar(v.id)}
                      aria-label={`Elegir la versión de ${autorDeLaVersion(v)} del ${v.creada_en ? formatearFechaYHora(v.creada_en) : "—"}`}
                    />
                    <span className="text-label text-ink font-bold">{autorDeLaVersion(v)}</span>
                    <span className="text-caption text-ink-muted font-mono">{v.creada_en ? formatearFechaYHora(v.creada_en) : "—"}</span>
                    <span className="text-caption text-ink-hint font-mono">{palabras(v.palabras_agregadas, v.palabras_quitadas)}</span>
                    {v.vigente ? <Badge variant="success">Vigente</Badge> : null}
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={elegidas.length !== 2}
                  onClick={() => setComparando([Math.min(...elegidas), Math.max(...elegidas)] as [number, number])}
                >
                  Comparar
                </Button>
                {elegidas.length !== 2 ? <span className="text-caption text-ink-hint">Elige dos versiones para ver qué cambió.</span> : null}
              </div>
            </>
          )}

          {comparando !== null ? (
            <section aria-label="Cambios entre las dos versiones" className="border-border flex flex-col gap-2 rounded-xl border-[1.5px] p-3.5">
              {comparacion.isPending ? (
                <Skeleton className="h-16 w-full rounded-lg" />
              ) : comparacion.isError ? (
                <p className="text-label text-danger-soft-foreground">
                  {getApiErrorMessage(comparacion.error, "No pudimos comparar las versiones.")}
                </p>
              ) : (
                <>
                  <p className="text-caption text-ink-muted font-mono">{palabras(comparacion.data.agregadas, comparacion.data.quitadas)}</p>
                  <p className="text-body text-ink-body leading-relaxed text-pretty whitespace-pre-line">
                    {segmentosLegiblesDelDiff(comparacion.data.segmentos).map((tramo, i) =>
                      tramo.tipo === "added" ? (
                        <ins key={i} className="bg-success-soft text-success-soft-foreground rounded-sm no-underline">
                          {tramo.texto}
                        </ins>
                      ) : tramo.tipo === "removed" ? (
                        <del key={i} className="bg-danger-soft text-danger-soft-foreground rounded-sm line-through">
                          {tramo.texto}
                        </del>
                      ) : (
                        <span key={i}>{tramo.texto}</span>
                      ),
                    )}
                  </p>
                </>
              )}
            </section>
          ) : null}
        </section>
      </CollapsibleContent>
    </Collapsible>
  );
}
